// Главный компонент приложения

import { useState, useEffect } from 'react';
import { CalculatorProvider, useCalculator } from './context/CalculatorContext';
import { AppHeader, TabPanel, ThemeToggle } from './components/layout';
import { Button } from './components/buttons';
import { VoltageLossTab, HeatingTab } from './components/tabs';
import { AboutDialog, HelpDialog } from './components/info';
import { Calculator, Flame, Info, HelpCircle, RotateCcw } from 'lucide-react';
import './App.css';

// Хук для определения режима встраивания
function useEmbedMode() {
  const [isEmbedded, setIsEmbedded] = useState(false);

  useEffect(() => {
    // Проверяем несколько способов встраивания
    const checkEmbed = () => {
      // 1. Проверка URL параметра
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('embed') === 'true') {
        return true;
      }
      
      // 2. Проверка iframe
      if (window.self !== window.top) {
        return true;
      }
      
      // 3. Проверка WordPress REST API embed
      if (window.location.pathname.includes('/wp-json/') ||
          document.body.classList.contains('wp-embed-responsive')) {
        return true;
      }
      
      return false;
    };
    
    setIsEmbedded(checkEmbed());
  }, []);

  return isEmbedded;
}

// Вкладки
const tabs = [
  {
    id: 'voltage',
    label: 'Потеря напряжения',
    icon: <Calculator size={18} />,
    description: '⚡ Расчёт падения напряжения в кабельной линии при прохождении тока. Позволяет проверить соответствие нормативным требованиям (не более 5% для силовых сетей) и подобрать оптимальное сечение кабеля для минимизации потерь электроэнергии.'
  },
  {
    id: 'heating',
    label: 'Нагрев (длит. ток)',
    icon: <Flame size={18} />,
    description: '🔥 Определение максимального тока, который кабель может пропускать длительное время без перегрева. Расчёт учитывает способ прокладки, температуру среды, количество кабелей рядом и тип изоляции согласно ПУЭ.'
  },
];

// Компонент содержимого приложения
function AppContent() {
  const {
    activeTab,
    setActiveTab,
    reset,
  } = useCalculator();

  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const isEmbedded = useEmbedMode();

  return (
    <div className={`app${isEmbedded ? ' app--embedded' : ''}`}>
      {!isEmbedded && (
        <AppHeader
          title="Калькулятор сечения кабеля"
          actions={
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <ThemeToggle />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsHelpOpen(true)}
                icon={<HelpCircle size={18} />}
              >
                Справка
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAboutOpen(true)}
                icon={<Info size={18} />}
              >
                О программе
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={reset}
                icon={<RotateCcw size={18} />}
              >
                Сбросить
              </Button>
            </div>
          }
        />
      )}
      
      {/* Панель действий для встроенного режима */}
      {isEmbedded && (
        <div className="app__embedded-actions">
          <ThemeToggle />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsHelpOpen(true)}
            icon={<HelpCircle size={16} />}
          >
            Справка
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAboutOpen(true)}
            icon={<Info size={16} />}
          >
            О программе
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            icon={<RotateCcw size={16} />}
          >
            Сброс
          </Button>
        </div>
      )}
      
      <main className="app__main">
        <TabPanel
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as 'voltage' | 'heating')}
        >
          {activeTab === 'voltage' && <VoltageLossTab />}
          {activeTab === 'heating' && <HeatingTab />}
        </TabPanel>
      </main>

      {/* Модальные окна */}
      <AboutDialog isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
      <HelpDialog isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
    </div>
  );
}

// Главный компонент с провайдером
function App() {
  return (
    <CalculatorProvider>
      <AppContent />
    </CalculatorProvider>
  );
}

export default App;
